import iconGear from './assets/art/icon-gear.svg?raw'
import { svgFromRaw } from './assets/art/svg'
import { LULLABY_MAX_MS, SFX, VOICE } from './audio/catalog'
import { AudioEngine } from './audio/engine'
import { el } from './dom'
import { listGames } from './games/registry'
import type { GameId, Unmount } from './games/types'
import { createHome } from './shell/home'
import { COPY } from './shell/copy'
import { createGuidedBlurb } from './shell/guided-blurb'
import { createParentGate } from './shell/parent-gate'
import { createParentPanel } from './shell/parent-panel'
import { createWinddown } from './shell/winddown'
import { mountGameScreen } from './shell/game-chrome'
import {
  loadGuidedAccessSeen,
  loadSettings,
  resetColorsLevel,
  saveDurationMin,
  saveGuidedAccessSeen,
  saveMuted,
} from './storage/settings'
import { SessionTimer, type TimerPhase } from './timer/session-timer'

type View = 'home' | 'game' | 'winddown'

export function createApp(root: HTMLElement): void {
  const settings = loadSettings()
  const audio = new AudioEngine(settings.muted)
  const timer = new SessionTimer(settings.durationMin)
  const games = listGames()

  let view: View = 'home'
  let unmountGame: Unmount | null = null
  let currentGameId: GameId | null = null
  let lastPhase: TimerPhase = 'idle'
  let lullabyTimer = 0
  let lullabyHandle: { stop: () => void } | null = null

  const home = createHome({
    games,
    onStart: () => {
      const unlocking = audio.unlock()
      home.setUnlocked(true)
      home.setMuted(audio.isMuted())
      void unlocking.then(() => {
        audio.play(SFX.tap)
        audio.play(VOICE.batDauChoi)
      })
    },
    onOpenGame: (id) => openGame(id),
  })
  home.setMuted(settings.muted)

  const gameView = el('section', {
    className: 'game-view',
    testId: 'game-view',
    attrs: { hidden: '' },
  })

  const winddown = createWinddown()
  const warningPulse = el('span', {
    className: 'warning-pulse',
    attrs: { hidden: '', 'aria-hidden': 'true' },
  })
  const gear = createGearButton()

  const gate = createParentGate({
    onUnlock: () => {
      gate.close()
      if (view === 'winddown') {
        endToHome()
        return
      }
      if (loadGuidedAccessSeen()) {
        panel.open()
        return
      }
      blurb.open()
    },
  })

  const blurb = createGuidedBlurb({
    onDone: () => {
      saveGuidedAccessSeen()
      blurb.close()
      panel.open()
    },
  })

  const panel = createParentPanel({
    durationMin: settings.durationMin,
    muted: settings.muted,
    onDuration: (minutes) => {
      saveDurationMin(minutes)
      timer.setDurationMinutes(minutes)
      panel.setDuration(minutes)
    },
    onMute: (muted) => {
      saveMuted(muted)
      audio.setMuted(muted)
      panel.setMuted(muted)
      home.setMuted(muted)
    },
    onEndSession: () => {
      panel.close()
      endToHome()
    },
    onResetLevels: () => {
      resetColorsLevel()
      if (view === 'game' && currentGameId === 'colors') openGame('colors')
    },
    onDone: () => panel.close(),
  })

  gear.addEventListener('click', () => {
    if (!panel.root.hidden) return
    if (!blurb.root.hidden) return
    gate.open()
  })

  root.append(
    home.root,
    gameView,
    winddown.root,
    warningPulse,
    gear,
    gate.root,
    blurb.root,
    panel.root,
  )
  applyTimer(timer.snapshot())

  window.setInterval(() => {
    applyTimer(timer.tick(performance.now()))
  }, 200)

  function openGame(id: GameId): void {
    if (view === 'winddown') return
    if (!audio.isUnlocked()) return
    const game = games.find((item) => item.id === id)
    if (!game || !game.playable) return
    if (view === 'game') {
      unmountGame?.()
      unmountGame = null
    }
    view = 'game'
    currentGameId = id
    home.root.hidden = true
    winddown.hide()
    gameView.hidden = false
    unmountGame = mountGameScreen(gameView, game, {
      audio,
      requestHome: () => goHome(),
    })
    applyTimer(timer.enterGame(performance.now()))
    audio.play(SFX.tap)
  }

  function goHome(): void {
    if (view === 'winddown') return
    unmountGame?.()
    unmountGame = null
    currentGameId = null
    view = 'home'
    gameView.hidden = true
    gameView.replaceChildren()
    winddown.hide()
    home.root.hidden = false
    applyTimer(timer.leaveToHome(performance.now()))
  }

  function endToHome(): void {
    stopWinddownAudio()
    unmountGame?.()
    unmountGame = null
    currentGameId = null
    view = 'home'
    gameView.hidden = true
    gameView.replaceChildren()
    winddown.hide()
    home.root.hidden = false
    applyTimer(timer.reset())
  }

  function enterWinddown(): void {
    unmountGame?.()
    unmountGame = null
    currentGameId = null
    view = 'winddown'
    home.root.hidden = true
    gameView.hidden = true
    panel.close()
    blurb.close()
    winddown.show()
    audio.stopAll()
    audio.play(VOICE.beNghiThoi)
    lullabyHandle = audio.play(SFX.lullaby, { loop: true, gain: 0.7 })
    lullabyTimer = window.setTimeout(() => {
      lullabyHandle?.stop()
      lullabyHandle = null
    }, LULLABY_MAX_MS)
  }

  function stopWinddownAudio(): void {
    if (lullabyTimer) {
      window.clearTimeout(lullabyTimer)
      lullabyTimer = 0
    }
    lullabyHandle?.stop()
    lullabyHandle = null
    audio.stopAll()
  }

  function applyTimer(snap: ReturnType<SessionTimer['snapshot']>): void {
    root.classList.toggle('is-warning', snap.warning)
    warningPulse.hidden = !snap.warning
    if (snap.phase === 'winddown' && lastPhase !== 'winddown') {
      enterWinddown()
    }
    lastPhase = snap.phase
  }
}

function createGearButton(): HTMLButtonElement {
  const btn = el('button', {
    className: 'gear',
    testId: 'parent-gear',
    attrs: { type: 'button', 'aria-label': COPY.gear },
  })
  btn.append(svgFromRaw(iconGear, 'gear__icon'))
  return btn
}
